import {
  Disconnectable,
  ElementEventCallback,
  ElementExistsCallback,
  ElementsEventObserverPort,
  ElementsExistsObserverPort,
} from '../../../ports'

export class ElementsEventObserver implements ElementsEventObserverPort {
  constructor(
    private _document: Document,
    private readonly elementsExistsObserver: ElementsExistsObserverPort,
    private abortController: AbortController = new AbortController()
  ) {}

  observeOnce<K extends keyof HTMLElementEventMap>(
    elementsSelector: string,
    event: K,
    eventCallback: ElementEventCallback<K>
  ): Disconnectable {
    return this.observe(elementsSelector, event, eventCallback, { once: true })
  }

  observe<K extends keyof HTMLElementEventMap>(
    elementsSelector: string,
    event: K,
    eventCallback: ElementEventCallback<K>,
    options: { once: boolean } = { once: false }
  ): Disconnectable {
    const disconnect = this.elementsExistsObserver.observeOnce(
      elementsSelector,
      this.createCallback<K>(event, eventCallback, {
        ...options,
        signal: this.abortController.signal,
      })
    )
    return {
      disconnect: () => {
        this.abortController.abort('Abort by call to disconnect')
        disconnect.disconnect()
      },
    }
  }

  private createCallback<K extends keyof HTMLElementEventMap>(
    event: K,
    eventCallback: ElementEventCallback<K>,
    options?: AddEventListenerOptions
  ): ElementExistsCallback {
    return (element: HTMLElement) => {
      element.addEventListener(
        event,
        (event) => void eventCallback(element, event),
        { ...options }
      )
    }
  }
}
